import React, { FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import "../../index.css"
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { clearState } from '../../store/passwordSlice';
import CustomTextField from '../../components/FormFields/CustomTextField';
import { forgotPassword } from '../../store/passwordThunk';
import { ApiStatus } from '../../utils/api.types';
import { IForgotPasswordFormInputs } from '../../utils/password.types';
import { FormHelperText, Link } from '@mui/material';
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

// Schema to validate forgot password form.
const schema = yup.object().shape({
    email: yup.string().email().required('Email is required.'),
});

interface LoginProps {
    setType: (k: any) => void;
}
/**
 * Component for Forgot Password.
 */
const ForgotPassword: FC<LoginProps> =({setType}) => {
    const methods = useForm<IForgotPasswordFormInputs>({
        resolver: yupResolver(schema),
    });

    const { status, errorMessage } = useAppSelector((state) => state.password);

    const dispatch = useAppDispatch();
    const navigate = useNavigate()

    /**
     * Method to handle forgot password form submit.
     */
    const onSubmit = (data: IForgotPasswordFormInputs) => {
        dispatch(forgotPassword(data));
    };

    /**
     * Clear state on reset password page load.
     */
    useEffect(() => {
        return () => {
            dispatch(clearState());
        };
    }, [dispatch]);

    /**
     * Update UI based on the state(Success or Error).
     */
    useEffect(() => {
        // If reset password is successful then logout the user and then
        // redirect the user to login page.
        if (status === ApiStatus.SUCCESS) {
            dispatch(clearState());

            navigate('/reset-password', { replace: true });
        }
    }, [status, dispatch, navigate]);

    return (
        <div>
            <Typography
                style={{ marginBottom: 12 }}
                component={Link}
                onClick={() => setType('0')}
                variant="body1"
                sx={{ textDecoration: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                color="#aaa"
            >
                <ArrowBackIosIcon sx={{ fontSize: 14 }} />
                Back to Login
            </Typography>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, marginBottom: 14 }}>
                <h3 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#111' }}>Forgot Password</h3>
            </div>

            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
                    <div style={{ marginBottom: 10 }}>
                        <label style={labelStyle}>Email address</label>
                        <CustomTextField
                            label=""
                            placeholder="Enter email"
                            margin="none"
                            fullWidth
                            size="small"
                            sx={textFieldSx}
                            name="email"
                            type="email"
                            required
                        />
                    </div>

                    {status === ApiStatus.FAILED && errorMessage && (
                        <FormHelperText error sx={{ mb: 1 }}>{errorMessage}</FormHelperText>
                    )}

                    <button
                        type="submit"
                        disabled={status === ApiStatus.LOADING}
                        style={{
                            width: '100%',
                            background: '#059669',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 8,
                            padding: '10px 16px',
                            fontSize: 15,
                            fontWeight: 600,
                            cursor: 'pointer',
                        }}
                    >
                        {status === ApiStatus.LOADING ? 'Sending...' : 'Submit'}
                    </button>
                </form>
            </FormProvider>
        </div>
    )
}

const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#333',
    marginBottom: 6,
};

const textFieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        background: '#fff',
        '& fieldset': { borderColor: '#d1d5db' },
        '&:hover fieldset': { borderColor: '#059669' },
        '&.Mui-focused fieldset': { borderColor: '#059669' },
    },
};

export default ForgotPassword;
